// src/pages/AdminPageTemplates.js
import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/api';

const BLOCKS = [
  { key: 'dashboard-intro', label: '📋 Dashboard Introduction', desc: 'Shown at the top of the dashboard for all users. Use this to describe the system or provide guidance.' },
  { key: 'dashboard-files', label: '📁 File Transfers Section', desc: 'Shown above the file transfers summary on the dashboard.' },
  { key: 'dashboard-credentials', label: '🔐 Credential Transfers Section', desc: 'Shown above the credential transfers summary on the dashboard.' },
];

function BlockEditor({ block, saved, onSaved }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (saved) { setTitle(saved.title || ''); setBody(saved.body || ''); }
  }, [saved]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(`mgmt/page-templates/${block.key}`, { method: 'PUT', body: JSON.stringify({ title, body }) });
      onSaved();
      showToast('✅ Saved');
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div className="card-header"><span className="card-title">{block.label}</span></div>
      <div className="card-body">
        <p style={{ fontSize: '13px', color: 'var(--g500)', marginBottom: '16px' }}>{block.desc}</p>
        <div className="form-group">
          <label className="form-label">Heading</label>
          <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Section heading…" />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Body text</label>
          <textarea className="form-textarea" rows={4} value={body} onChange={e => setBody(e.target.value)} placeholder="Descriptive text shown to users…" style={{ resize: 'vertical' }} />
        </div>
        {saved?.updatedAt && (
          <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '8px' }}>
            Last saved by {saved.updatedBy} on {new Date(saved.updatedAt).toLocaleString('en-GB')}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save'}</button>
        </div>
        {toast && <div style={{ marginTop: '8px', fontSize: '13px', color: toast.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{toast}</div>}
      </div>
    </div>
  );
}

export default function AdminPageTemplates() {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await apiFetch('mgmt/page-templates'); setTemplates(d.templates || {}); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-eyebrow">Administration</div>
      <h1 className="page-title">Page Templates</h1>
      <p className="page-subtitle">Edit the text blocks shown on the dashboard. Changes take effect immediately for all users.</p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        BLOCKS.map(block => (
          <BlockEditor key={block.key} block={block} saved={templates[block.key] || null} onSaved={load} />
        ))
      )}
    </div>
  );
}
